import React from "react";
import { Button, Chip, Box, Typography } from "@material-ui/core";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AssignmentIcon from "@material-ui/icons/Assignment";
import { formatErpDocumentNumber } from "../../utils/formatErpDocumentNumber";
import { openErpDocumentInNewTab } from "../../utils/erpUrls";
import {
  ErpDialogShell,
  ErpHeroHeader,
  ErpLineItemsTable,
  ErpTotalBar,
  ErpOpenInErpButton,
  formatErpDate
} from "../ErpDialog/ErpDialogShared";
import ErpCompactInfo from "../ErpDialog/ErpCompactInfo";
import ErpPaymentSection from "../ErpDialog/ErpPaymentSection";
import erpClientDisplayName from "../../utils/erpClientDisplayName";
import { getErpPaymentSummary } from "../../utils/erpDocumentPayment";

const ESTADO_LABEL = {
  PENDIENTE: "Pendiente",
  CONFIRMADA: "Confirmada",
  PROCESADA: "Procesada"
};

const ErpDocumentDetailDialog = ({
  open,
  onClose,
  type,
  item,
  loading,
  erpFrontendUrl
}) => {
  const isFactura = type === "facturas-venta";
  const id = isFactura ? item?.facturaVentaId : item?.ordenId;
  const numero = isFactura ? item?.numeroFactura : item?.numeroOrden;
  const docLabel = isFactura ? "Factura de venta" : "Orden de venta";
  const symbol = item?.tenantCurrency?.symbol || "C$";
  const detalles = item?.detalles || [];
  const paymentSummary = item
    ? getErpPaymentSummary(
        item,
        type,
        !isFactura ? item.facturaCobro : null
      )
    : null;

  const linkedFacturaLabel =
    !isFactura && item?.facturaCobro
      ? formatErpDocumentNumber(
          item.facturaCobro.numeroFactura,
          item.facturaCobro.sucursalSede || item.sucursalSede
        )
      : null;

  const docNumber = item
    ? formatErpDocumentNumber(numero, item.sucursalSede)
    : "";

  const chips = [];
  if (isFactura && item?.anulado) {
    chips.push(<Chip key="void" label="Anulada" size="small" color="secondary" />);
  }
  if (!isFactura && item?.estado) {
    chips.push(
      <Chip
        key="estado"
        label={ESTADO_LABEL[item.estado] || item.estado}
        size="small"
        color="primary"
        variant="outlined"
      />
    );
  }
  if (item?.tipoFactura?.nombre) {
    chips.push(
      <Chip key="tipo" label={item.tipoFactura.nombre} size="small" variant="outlined" />
    );
  }
  if (paymentSummary && !paymentSummary.voided) {
    chips.push(
      <Chip
        key="cobro"
        label={paymentSummary.statusLabel}
        size="small"
        color={
          paymentSummary.isPaid
            ? "primary"
            : paymentSummary.isPartial
            ? "default"
            : "secondary"
        }
      />
    );
  }

  const infoFields = item
    ? [
        { label: "Fecha", value: formatErpDate(item.fecha) },
        {
          label: "Sucursal",
          value: item.sucursalSede?.name || item.sucursalSede?.abbreviation
        },
        {
          label: "Cliente",
          value: item.cliente
            ? erpClientDisplayName(item.cliente)
            : null
        },
        ...(item.concepto ? [{ label: "Concepto", value: item.concepto }] : []),
        ...(item.vendedor
          ? [
              {
                label: "Vendedor",
                value: [item.vendedor.firstName, item.vendedor.lastName]
                  .filter(Boolean)
                  .join(" ")
              }
            ]
          : [])
      ]
    : [];

  const handleOpenInErp = () => {
    openErpDocumentInNewTab(erpFrontendUrl, type, id);
  };

  return (
    <ErpDialogShell
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={docLabel}
      subtitle={docNumber}
      icon={
        isFactura ? (
          <ReceiptIcon color="primary" fontSize="large" />
        ) : (
          <AssignmentIcon color="primary" fontSize="large" />
        )
      }
      loading={loading}
      actions={
        <>
          <Button onClick={onClose}>Cerrar</Button>
          <ErpOpenInErpButton
            onClick={handleOpenInErp}
            disabled={!erpFrontendUrl || !id || !item}
            label={isFactura ? "Abrir factura en el ERP" : "Abrir orden en el ERP"}
          />
        </>
      }
    >
      {!loading && item && (
        <>
          <ErpHeroHeader title={docNumber} chips={chips.length ? chips : undefined} />
          <Box mb={2}>
            <ErpCompactInfo fields={infoFields} />
          </Box>
          {paymentSummary && (
            <ErpPaymentSection
              summary={paymentSummary}
              linkedFacturaLabel={linkedFacturaLabel}
            />
          )}
          <Box mb={2}>
            <ErpLineItemsTable lines={detalles} currencySymbol={symbol} />
          </Box>
          <ErpTotalBar label="Total del documento" amount={item.total} currencySymbol={symbol} />
        </>
      )}
      {!loading && !item && (
        <Typography align="center" color="textSecondary" style={{ padding: 32 }}>
          No se pudo cargar el documento.
        </Typography>
      )}
    </ErpDialogShell>
  );
};

export default ErpDocumentDetailDialog;
